import React from 'react';
import { ChatHeader } from '../components/Chat/ChatHeader';
import { MessageList } from '../components/Chat/MessageList';
import { MessageInput } from '../components/Chat/MessageInput';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useChat } from '../hooks/useChat';
import { useAuth } from '../context/AuthContext';
import { AccessDenied } from '../components/Chat/AccessDenied';
import { ChatSkeleton } from '../components/Chat/ChatSkeleton';
import { ImageGallery } from '../components/Chat/ImageGallery';
import { BASE_DOMAIN } from '../api/config'; 


export const ChatPage: React.FC<{ isMobile?: boolean }> = ({ isMobile }) => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    
    // Вся магия теперь тут
    const chat = useChat(id, currentUser);

    // ЕСЛИ ЕЩЕ ГРУЗИТСЯ
    if (!chat.conversation && !chat.error) {
        return <ChatSkeleton />;
    }

    // ЕСЛИ ДОСТУП ЗАПРЕЩЕН (403)
    if (chat.error === 'Access Denied') {
        return <AccessDenied />;
    }


    //собираем все img беседы в один массив
    const allChatImages = chat.messages
        .filter(m => {
            const id = m.id;
            const hasError = chat.imageErrors[id]; // стейт с ошибками картинок
            const url = (m as any).preview || (m.payload?.filePath ? `${BASE_DOMAIN}${m.payload.filePath}` : null);
            
            return url && !hasError; // Пропускаем только если есть URL и нет ошибки
        })
        .map(m => (m as any).preview || `${BASE_DOMAIN}${m.payload?.filePath}`)
        .filter(Boolean) as string[];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden',  minWidth: 0 }}>
            {/* Header */}
            <ChatHeader
                conversation={chat.conversation}
                chatPartner={chat.chatPartner}
                isContactOnline={chat.isContactOnline}
                isMobile={isMobile}
                onBack={() => navigate('/dashboard')}
                currentUserId={currentUser?.id ?? null}
                // Из удалённой группы или после выхода возвращаемся к списку чатов.
                onGroupRemoved={() => navigate('/dashboard')}
            />


            {/* Messages Body */}
            <MessageList 
                messages={chat.messages}
                conversation={chat.conversation}
                currentUser={currentUser}
                imageErrors={chat.imageErrors}
                setImageErrors={chat.setImageErrors}
                setSelectedImage={chat.setSelectedImage}
                messagesEndRef={chat.messagesEndRef}
                setNewMessageText = {chat.setNewMessageText}
                replyTo = {chat.replyTo}
                setReplyTo = {chat.setReplyTo}
                loadMore={chat.loadMore} 
                hasMore={chat.hasMore} 
                loadingMore={chat.loadingMore} 
                scrollToBottom={chat.scrollToBottom}
                onRetry={chat.retryMessage}
            />


            {/* Индикатор печати */}
            {chat.isTyping && (
                <Box sx={{ px: 2, py: 0.5, bgcolor: 'rgba(255,255,255,0.8)', bottom: 80, left: 20, borderRadius: '10px', zIndex: 5 }}>
                    <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'primary.main' }}>
                        {chat.conversation?.contact?.mainName} печатает...
                    </Typography>
                </Box>
            )}

            {chat.replyTo && (
                <Box sx={{
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: 'rgba(0,0,0,0.03)',
                    borderLeft: '4px solid',
                    borderColor: 'primary.main'
                }}>
                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                        <Typography variant="caption" color="primary" fontWeight="bold">
                            Ответ на сообщение
                        </Typography>
                        <Typography variant="body2" noWrap sx={{ opacity: 0.8 }}>
                            {chat.replyTo.text}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => chat.setReplyTo(null)}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}

            <MessageInput
                handleSend={(text) => chat.handleSend(text)} // Передаем текст из инпута в функцию отправки
                takePhoto={chat.takePhoto}
                isMobile={isMobile}
                socket={chat.socket}
                id={id}
                currentUser={currentUser}
            />
                
            {/*  НАША ГАЛЕРЕЯ */}
            <ImageGallery 
                open={!!chat.selectedImage}
                images={allChatImages}
                currentImage={chat.selectedImage}
                onClose={() => chat.setSelectedImage(null)}
                onSelect={(url) => chat.setSelectedImage(url)}
            />
        </Box>
    );
};

export default ChatPage;


